/* oxlint-disable next/no-img-element -- The small brand SVG is already an optimized static asset. */
import {
  ArrowRight,
  Check,
  CheckCircle2,
  CloudUpload,
  FilePlus2,
  Folder,
  FolderPlus,
  KeyRound,
  LoaderCircle,
  LogOut,
  Pencil,
  RefreshCw,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IdeaForm } from './components/IdeaForm';
import { InstallButton } from './components/InstallButton';
import {
  ApiError,
  beginBilling,
  chooseFolder,
  disconnectDropbox,
  dropboxStartUrl,
  getFolders,
  getStatus,
  uploadDocument,
  type AccountStatus,
  type DropboxFolder,
} from './lib/api';
import {
  cleanFilenameBase,
  destinationName,
  formatBytes,
  MAX_BATCH_BYTES,
  MAX_QUEUE_FILES,
  splitFilename,
  uniqueDestinationName,
  validateDocument,
} from './lib/files';
import { trackPageView } from './lib/analytics';

type QueueStatus = 'ready' | 'uploading' | 'done' | 'error';

type QueueItem = {
  id: string;
  file: File;
  baseName: string;
  extension: string;
  status: QueueStatus;
  progress: number;
  error?: string;
  uploadedPath?: string;
  editing: boolean;
};

const emptyStatus: AccountStatus = {
  connected: false,
  configured: false,
  destinationPath: null,
  entitled: false,
  remainingUploads: 0,
  billingConfigured: false,
  canManageBilling: false,
};

const acceptedDocuments = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.rtf', '.odt', '.ods', '.odp', '.md', '.json',
].join(',');

function cleanFolderName(value: string): string {
  return cleanFilenameBase(value).slice(0, 80);
}

function messageFrom(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function initialNotice(): string {
  const query = new URLSearchParams(window.location.search);
  if (query.get('dropbox') === 'cancelled') return 'Dropbox login was cancelled.';
  if (query.get('dropbox') === 'expired') return 'Dropbox login expired. Try again.';
  if (query.get('checkout') === 'success') return 'Payment received. Access will update in a moment.';
  return '';
}

export function App() {
  useEffect(() => {
    trackPageView('documents-to-dropbox');
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef<QueueItem[]>([]);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousStepRef = useRef<number | null>(null);
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [notice, setNotice] = useState(initialNotice);
  const [folders, setFolders] = useState<DropboxFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('Documents');
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderLoading, setFolderLoading] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [sending, setSending] = useState(false);
  const [dragging, setDragging] = useState(false);
  const checkoutSucceeded = useMemo(
    () => new URLSearchParams(window.location.search).get('checkout') === 'success',
    [],
  );

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    const controller = new AbortController();
    getStatus(controller.signal)
      .then((next) => setStatus(next))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setStatus(emptyStatus);
        setNotice('Dropbox connection isn’t turned on in this build yet.');
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!checkoutSucceeded) return;
    const controller = new AbortController();
    let timer: number | undefined;
    let attempts = 0;

    const poll = async () => {
      try {
        const next = await getStatus(controller.signal);
        if (controller.signal.aborted) return;
        setStatus(next);
        if (next.entitled) {
          setNotice('Your plan is active. You can keep saving documents.');
          return;
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
      attempts += 1;
      if (attempts < 20) timer = window.setTimeout(() => void poll(), 1500);
      else setNotice('Payment received. Your plan is still activating; refresh this page in a moment.');
    };

    timer = window.setTimeout(() => void poll(), 1000);
    return () => {
      if (timer) window.clearTimeout(timer);
      controller.abort();
    };
  }, [checkoutSucceeded]);

  const currentStep = !status?.connected ? 1 : !status.destinationPath ? 2 : 3;

  useEffect(() => {
    if (!status) return;
    const previous = previousStepRef.current;
    previousStepRef.current = currentStep;
    if (previous === null || previous === currentStep) return;
    const frame = requestAnimationFrame(() => stepHeadingRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [currentStep, status]);

  const loadFolders = useCallback(async () => {
    setFolderLoading(true);
    setNotice('');
    try {
      const result = await getFolders();
      setFolders(result.folders);
      setSelectedFolder(result.destinationPath || result.folders[0]?.path || '');
    } catch (error) {
      setNotice(messageFrom(error, 'Dropbox folders are unavailable.'));
    } finally {
      setFolderLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status?.connected && !status.destinationPath) {
      queueMicrotask(() => void loadFolders());
    }
  }, [loadFolders, status?.connected, status?.destinationPath]);

  const saveFolder = useCallback(async (input: { action: 'select'; path: string } | { action: 'create'; name: string }) => {
    setFolderBusy(true);
    setNotice('');
    try {
      const result = await chooseFolder(input);
      setStatus((current) => current ? { ...current, destinationPath: result.destinationPath } : current);
      setNotice(`Ready. Documents will go to ${result.destinationPath}.`);
      return result.destinationPath;
    } catch (error) {
      const message = messageFrom(error, 'The folder could not be saved.');
      setNotice(message);
      throw error;
    } finally {
      setFolderBusy(false);
    }
  }, []);

  useEffect(() => {
    const context = document.modelContext;
    if (!context?.registerTool) return;
    const lifecycle = new AbortController();
    const register = (tool: WebMcpTool) => {
      try {
        void Promise.resolve(context.registerTool(tool, { signal: lifecycle.signal })).catch(() => undefined);
      } catch {
        // The visible app keeps working in browsers with partial WebMCP support.
      }
    };

    register({
      name: 'get_documents_dropbox_setup',
      title: 'Check document upload setup',
      description: 'Read the current Dropbox connection, destination folder, and upload-access state.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: false },
      execute: () => ({
        connected: Boolean(status?.connected),
        destinationPath: status?.destinationPath || null,
        setupStep: currentStep,
        paidAccess: Boolean(status?.entitled),
        remainingFreeUploads: status?.remainingUploads ?? null,
      }),
    });

    register({
      name: 'create_documents_dropbox_folder',
      title: 'Create document folder',
      description: 'Create and select one folder inside the connected BuckSimple Dropbox app folder.',
      inputSchema: {
        type: 'object',
        properties: { name: { type: 'string', minLength: 1, maxLength: 80 } },
        required: ['name'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, untrustedContentHint: false },
      async execute(input) {
        if (!status?.connected) throw new Error('Connect Dropbox first.');
        const name = typeof input === 'object' && input && 'name' in input
          ? cleanFolderName(String((input as { name: unknown }).name))
          : '';
        if (!name) throw new Error('A valid folder name is required.');
        const destinationPath = await saveFolder({ action: 'create', name });
        return { destinationPath };
      },
    });

    return () => lifecycle.abort();
  }, [currentStep, saveFolder, status?.connected, status?.destinationPath, status?.entitled, status?.remainingUploads]);

  function addFiles(files: FileList | File[]) {
    const incoming = Array.from(files);
    if (!incoming.length) return;

    const activeQueue = queue.filter((item) => item.status !== 'done');
    let remainingSlots = MAX_QUEUE_FILES - activeQueue.length;
    let batchBytes = activeQueue.reduce((sum, item) => sum + item.file.size, 0);
    const usedNames = new Set(queue.map((item) => destinationName(item.baseName, item.extension)));
    const accepted: QueueItem[] = [];
    const errors: string[] = [];

    for (const file of incoming) {
      if (remainingSlots <= 0) {
        errors.push(`You can send up to ${MAX_QUEUE_FILES} documents at once.`);
        break;
      }
      const validation = validateDocument(file);
      if (!validation.ok) {
        errors.push(validation.reason);
        continue;
      }
      if (batchBytes + file.size > MAX_BATCH_BYTES) {
        errors.push('This batch would be over 500 MB. Send the current files first.');
        break;
      }
      const uniqueName = uniqueDestinationName(file.name, usedNames);
      const split = splitFilename(uniqueName);
      usedNames.add(uniqueName);
      accepted.push({
        id: crypto.randomUUID(),
        file,
        baseName: split.baseName,
        extension: split.extension,
        status: 'ready',
        progress: 0,
        editing: false,
      });
      batchBytes += file.size;
      remainingSlots -= 1;
    }

    if (accepted.length) {
      setQueue((current) => [...current, ...accepted]);
      setNotice(`${accepted.length} ${accepted.length === 1 ? 'document' : 'documents'} ready.`);
    }
    if (errors.length) setNotice(errors.slice(0, 3).join(' '));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function updateItem(id: string, update: Partial<QueueItem>) {
    setQueue((current) => current.map((item) => item.id === id ? { ...item, ...update } : item));
  }

  async function sendItem(item: QueueItem): Promise<'saved' | 'checkout' | 'quota' | 'failed'> {
    const targetName = destinationName(item.baseName, item.extension);
    updateItem(item.id, { status: 'uploading', progress: 0, error: undefined, editing: false });
    try {
      const result = await uploadDocument(item.file, targetName, (progress) => updateItem(item.id, { progress }));
      updateItem(item.id, { status: 'done', progress: 100, uploadedPath: result.path });
      setStatus((current) => current ? {
        ...current,
        remainingUploads: result.remainingUploads ?? (current.entitled ? current.remainingUploads : Math.max(0, current.remainingUploads - 1)),
      } : current);
      return 'saved';
    } catch (error) {
      const checkout = error instanceof ApiError && error.checkoutRequired;
      const quota = error instanceof ApiError && error.status === 429;
      if (checkout) setStatus((current) => current ? { ...current, remainingUploads: 0 } : current);
      const message = checkout
        ? 'Your included save is used. Continue to checkout to keep sending.'
        : quota && error.resetAt
          ? `${error.message} It resets ${new Date(error.resetAt * 1000).toLocaleDateString()}.`
          : messageFrom(error, 'This document could not be sent.');
      updateItem(item.id, { status: 'error', error: message });
      return checkout ? 'checkout' : quota ? 'quota' : 'failed';
    }
  }

  async function sendAll() {
    const candidates = queueRef.current.filter((item) => item.status === 'ready' || item.status === 'error');
    if (!candidates.length || sending) return;
    setSending(true);
    setNotice('Sending your documents…');
    let completed = 0;
    for (const item of candidates) {
      const outcome = await sendItem(item);
      if (outcome === 'saved') completed += 1;
      if (outcome === 'checkout' || outcome === 'quota') break;
    }
    setSending(false);
    setNotice(
      completed === candidates.length
        ? `${completed} ${completed === 1 ? 'document' : 'documents'} saved to Dropbox.`
        : `${completed} saved. Check the file marked Try again.`,
    );
  }

  async function retryItem(id: string) {
    const item = queueRef.current.find((candidate) => candidate.id === id);
    if (!item || sending) return;
    setSending(true);
    const outcome = await sendItem(item);
    setSending(false);
    setNotice(outcome === 'saved' ? 'Document saved to Dropbox.' : 'That document still needs attention.');
  }

  async function disconnect() {
    setNotice('');
    try {
      await disconnectDropbox();
      setQueue([]);
      setFolders([]);
      setStatus((current) => current ? { ...emptyStatus, configured: current.configured } : emptyStatus);
      setNotice('Dropbox disconnected.');
    } catch (error) {
      setNotice(messageFrom(error, 'Dropbox could not be disconnected.'));
    }
  }

  async function openBilling(kind: 'checkout' | 'portal') {
    setNotice('');
    try {
      window.location.assign(await beginBilling(kind));
    } catch (error) {
      setNotice(messageFrom(error, 'Billing is unavailable.'));
    }
  }

  const readyCount = queue.filter((item) => item.status === 'ready' || item.status === 'error').length;
  const finishedCount = queue.filter((item) => item.status === 'done').length;
  const totalBytes = useMemo(() => queue.reduce((sum, item) => sum + item.file.size, 0), [queue]);
  const hasAccess = Boolean(status?.entitled || (status?.remainingUploads ?? 0) > 0);

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="https://bucksimple.com/" aria-label="BuckSimple home">
          <img className="brand-logo" src="/favicon.svg" alt="" width={40} height={40} />
          <span className="brand-wordmark">BuckSimple</span>
        </a>
        <InstallButton />
      </header>

      <main>
        <section className="intro" aria-labelledby="page-title">
          <p className="price-line"><span>ONE SIMPLE JOB</span> PICK FILES · CHOOSE A FOLDER · SEND</p>
          <h1 id="page-title">Documents <span>→</span> Dropbox</h1>
          <p>Pick files. Tap send. Done.</p>
        </section>

        <nav className="steps" aria-label="Setup progress">
          {[['1', 'Login'], ['2', 'Folder'], ['3', 'Send']].map(([number, label], index) => {
            const step = index + 1;
            return (
              <div className={step === currentStep ? 'step is-current' : step < currentStep ? 'step is-done' : 'step'} key={number}>
                <span>{step < currentStep ? <Check aria-label="Done" /> : number}</span>
                <small>{label}</small>
              </div>
            );
          })}
        </nav>

        <section className="work-card" aria-label="Documents to Dropbox">
          {!status ? (
            <div className="center-state" aria-live="polite">
              <LoaderCircle className="spinner" aria-hidden="true" />
              <h2>Checking Dropbox…</h2>
            </div>
          ) : !status.connected ? (
            <div className="center-state login-state">
              <span className="state-icon" aria-hidden="true"><KeyRound /></span>
              <p className="eyebrow">STEP 1 OF 3</p>
              <h2 ref={stepHeadingRef} tabIndex={-1}>Log in with Dropbox</h2>
              <p>Dropbox handles your login. BuckSimple never sees your password.</p>
              <button
                className="primary-button dropbox-button"
                type="button"
                disabled={!status.configured}
                onClick={() => window.location.assign(dropboxStartUrl())}
              >
                <span className="dropbox-glyph" aria-hidden="true"><i /><i /><i /><i /></span>
                Continue with Dropbox
                <ArrowRight aria-hidden="true" />
              </button>
              <p className="trust-note"><ShieldCheck aria-hidden="true" /> Your login and files stay private to your account.</p>
            </div>
          ) : !status.destinationPath ? (
            <div className="folder-state">
              <span className="state-icon" aria-hidden="true"><Folder /></span>
              <p className="eyebrow">STEP 2 OF 3</p>
              <h2 ref={stepHeadingRef} tabIndex={-1}>Choose a Dropbox folder</h2>
              <p>Pick one inside the BuckSimple app folder, or make a new one.</p>

              {folderLoading ? (
                <p className="loading-row"><LoaderCircle className="spinner" aria-hidden="true" /> Loading folders…</p>
              ) : (
                <div className="folder-controls">
                  {folders.length > 0 && (
                    <div className="field-block">
                      <label htmlFor="folder-select">Your folders</label>
                      <div className="control-row">
                        <select id="folder-select" value={selectedFolder} onChange={(event) => setSelectedFolder(event.target.value)}>
                          {folders.map((folder) => <option key={folder.path} value={folder.path}>{folder.name}</option>)}
                        </select>
                        <button
                          className="secondary-button"
                          type="button"
                          disabled={!selectedFolder || folderBusy}
                          onClick={() => void saveFolder({ action: 'select', path: selectedFolder }).catch(() => undefined)}
                        >
                          Use it
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="plain-divider"><span>{folders.length ? 'or make one' : 'Make your first folder'}</span></div>
                  <form
                    className="field-block"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const name = cleanFolderName(newFolderName);
                      if (!name) {
                        setNotice('Enter a folder name without slashes or special path characters.');
                        return;
                      }
                      void saveFolder({ action: 'create', name }).catch(() => undefined);
                    }}
                  >
                    <label htmlFor="new-folder">New folder name</label>
                    <div className="control-row">
                      <input
                        id="new-folder"
                        value={newFolderName}
                        maxLength={80}
                        onChange={(event) => setNewFolderName(event.target.value)}
                        autoComplete="off"
                      />
                      <button className="primary-button compact-button" type="submit" disabled={folderBusy}>
                        {folderBusy ? <LoaderCircle className="spinner" aria-hidden="true" /> : <FolderPlus aria-hidden="true" />}
                        Create
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ) : (
            <div className="send-state">
              <div className="account-row">
                <div>
                  <p className="eyebrow">STEP 3 OF 3</p>
                  <h2 ref={stepHeadingRef} tabIndex={-1}>Choose your documents</h2>
                </div>
                <button
                  className="folder-pill"
                  type="button"
                  onClick={() => setStatus((current) => current ? { ...current, destinationPath: null } : current)}
                  aria-label={`Change Dropbox folder. Current folder ${status.destinationPath}`}
                >
                  <Folder aria-hidden="true" />
                  <span>{status.destinationPath.split('/').filter(Boolean).at(-1) || 'Documents'}</span>
                  <Pencil aria-hidden="true" />
                </button>
              </div>

              {!hasAccess ? (
                <div className="paywall">
                  <span className="state-icon" aria-hidden="true"><CloudUpload /></span>
                  <h3>Your included save is used.</h3>
                  <p>To save another document, continue for $1/month. The plan includes up to 100 saves or 2 GiB per billing period. Cancel anytime.</p>
                  <button
                    className="primary-button"
                    type="button"
                    disabled={!status.billingConfigured}
                    onClick={() => void openBilling('checkout')}
                  >
                    Continue to secure checkout <ArrowRight aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    className="visually-hidden"
                    type="file"
                    multiple
                    accept={acceptedDocuments}
                    onChange={(event) => event.target.files && addFiles(event.target.files)}
                  />

                  <button
                    className={dragging ? 'file-picker is-dragging' : 'file-picker'}
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
                    onDragOver={(event) => event.preventDefault()}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDragging(false);
                      addFiles(event.dataTransfer.files);
                    }}
                  >
                    <span className="picker-icon" aria-hidden="true"><FilePlus2 /></span>
                    <strong>Choose documents</strong>
                    <small>PDF, Word, Excel, PowerPoint, text · 100 MB each</small>
                  </button>

                  {queue.length > 0 && (
                    <div className="queue" aria-label="Documents ready to send">
                      <div className="queue-heading">
                        <p><strong>{queue.length} selected</strong><span>{formatBytes(totalBytes)}</span></p>
                        {!sending && finishedCount === queue.length && (
                          <button type="button" onClick={() => setQueue([])}>Clear</button>
                        )}
                      </div>

                      <ul>
                        {queue.map((item) => {
                          const targetName = destinationName(item.baseName, item.extension);
                          return (
                            <li className={`queue-item is-${item.status}`} key={item.id}>
                              <span className="file-type" aria-hidden="true">{item.extension.slice(0, 4).toLocaleUpperCase()}</span>
                              <div className="file-details">
                                {item.editing ? (
                                  <div className="rename-control">
                                    <input
                                      value={item.baseName}
                                      maxLength={120}
                                      aria-label={`Rename ${targetName}`}
                                      onChange={(event) => updateItem(item.id, { baseName: event.target.value })}
                                      onBlur={() => updateItem(item.id, {
                                        baseName: cleanFilenameBase(item.baseName) || splitFilename(item.file.name).baseName,
                                        editing: false,
                                      })}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') event.currentTarget.blur();
                                        if (event.key === 'Escape') updateItem(item.id, {
                                          baseName: splitFilename(item.file.name).baseName,
                                          editing: false,
                                        });
                                      }}
                                    />
                                    <span>.{item.extension}</span>
                                  </div>
                                ) : (
                                  <button
                                    className="filename-button"
                                    type="button"
                                    disabled={item.status === 'uploading' || item.status === 'done'}
                                    onClick={() => updateItem(item.id, { editing: true })}
                                    aria-label={`Rename ${targetName}`}
                                  >
                                    <span>{targetName}</span><Pencil aria-hidden="true" />
                                  </button>
                                )}
                                <p>
                                  <span>{formatBytes(item.file.size)}</span>
                                  {item.status === 'ready' && <span>Ready</span>}
                                  {item.status === 'uploading' && <span>Sending {item.progress}%</span>}
                                  {item.status === 'done' && <span className="success-text"><CheckCircle2 aria-hidden="true" /> Saved</span>}
                                  {item.status === 'error' && <span className="error-text"><XCircle aria-hidden="true" /> Try again</span>}
                                </p>
                                {item.status === 'uploading' && <progress value={item.progress} max="100">{item.progress}%</progress>}
                                {item.error && <p className="file-error">{item.error}</p>}
                              </div>
                              {item.status === 'error' ? (
                                <button className="icon-button" type="button" onClick={() => void retryItem(item.id)} aria-label={`Try ${targetName} again`}>
                                  <RefreshCw aria-hidden="true" />
                                </button>
                              ) : (
                                <button
                                  className="icon-button"
                                  type="button"
                                  disabled={item.status === 'uploading'}
                                  onClick={() => setQueue((current) => current.filter((candidate) => candidate.id !== item.id))}
                                  aria-label={`Remove ${targetName}`}
                                >
                                  <Trash2 aria-hidden="true" />
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>

                      {readyCount > 0 && (
                        <button className="primary-button send-button" type="button" disabled={sending} onClick={() => void sendAll()}>
                          {sending ? <LoaderCircle className="spinner" aria-hidden="true" /> : <CloudUpload aria-hidden="true" />}
                          {sending ? 'Sending…' : `Send ${readyCount} to Dropbox`}
                        </button>
                      )}
                    </div>
                  )}

                  <p className="original-note"><ShieldCheck aria-hidden="true" /> Your originals stay unchanged. Renaming changes only the Dropbox copy.</p>
                </>
              )}
            </div>
          )}

          {notice && <output className="notice" aria-live="polite">{notice}</output>}
        </section>

        {status?.connected && (
          <div className="account-actions">
            <span>{status.displayName ? `Dropbox: ${status.displayName}` : 'Dropbox connected'}</span>
            <span className="account-plan">{status.entitled ? 'Plan active · up to 100 saves or 2 GiB this billing period' : `${status.remainingUploads} included save${status.remainingUploads === 1 ? '' : 's'} left`}</span>
            {status.canManageBilling && (
              <button type="button" onClick={() => void openBilling('portal')}>Manage plan</button>
            )}
            <button type="button" onClick={() => void disconnect()}><LogOut aria-hidden="true" /> Disconnect</button>
          </div>
        )}

        <section className="plain-language" aria-labelledby="formats-title">
          <h2 id="formats-title">Send the document you already have.</h2>
          <p>Use your phone’s Files picker for PDFs, Word documents, Excel spreadsheets, PowerPoint presentations, text files, CSVs, and OpenDocument files. Choose several and BuckSimple sends them one at a time.</p>
          <details id="privacy">
            <summary>Privacy &amp; security</summary>
            <div>
              <p>Dropbox handles your login, so BuckSimple never receives your Dropbox password. Only the files you choose are sent, and only after you tap Send.</p>
              <p>The secure server connection is designed to keep Dropbox tokens encrypted and out of the browser. Selected files are passed to Dropbox without keeping a BuckSimple copy.</p>
            </div>
          </details>
        </section>

        <IdeaForm />
      </main>

      <footer>
        <a href="https://bucksimple.com/">BuckSimple</a>
        <span>by Breakpoint Industries Inc</span>
        <a href="mailto:robh@bucksimple.com">Help</a>
      </footer>
    </div>
  );
}
